import {
    useLayoutEffect,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    TextInput,
    RowCard,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useSetGlobalState
} from "../../../context";
import {
    useNavigation,
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetMyProfilePayloadType
} from "../../../services/restAPI/actions/getMyProfile/types";
import type RootStackParamList from "../../../types/navigation";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    CircleCheckBig,
    ChevronRight,
    UserPenIcon,
    ChevronDown,
    ChevronLeft,
    LockKeyhole,
    LogOut,
    LogIn,
    Lock,
    Plus,
    Pen
} from "lucide-react-native";
import md5 from "md5";
import OneSignalService from "../../../services/OneSignalService";
import logo from "../../../assets/images/gmkonyalogo.png";
import {
    FooterVersionInfo
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";
import {
    passwordValidation
} from "../../../utils";

const Profile = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
    const logoutNavigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const setGlobalState = useSetGlobalState();

    const [showDetail,
        setShowDetail] = useState(false);
    const [loading,
        setLoading] = useState(true);
    const [data,
        setData] = useState<GetMyProfilePayloadType>();
    const [showLogout,
        setShowLogout] = useState(false);
    const [oldPassword,
        setOldPassword] = useState("");
    const [newPassword,
        setNewPassword] = useState("");
    const [newPasswordRepeat,
        setNewPasswordRepeat] = useState("");
    const [showSuccess,
        setShowSuccess] = useState(false);

    const changePasswordRef = useRef<IBottomSheetRef>(null);

    const inset = useSafeAreaInsets();

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.headerButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
            </View>
        });
    }, [navigation]);

    useEffect(() => {
        getProfileData();
    }, []);

    const getProfileData = () => {
        RESTService.action("GetMyProfile", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                }
                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const changePassword = () => {
        changePasswordRef.current?.close();
        RESTService.action("ChangePassword", {
            oldPassword: md5(oldPassword.trim()),
            newPassword: md5(newPassword.trim())
        })
            .then(() => {
                setShowSuccess(true);
            }).catch((err) => {
                console.log(err);
            });
    };

    const logout = () => {
        const handleLocalLogout = () => {
            OneSignalService.logout();
            setShowLogout(false);
            storage.clearAll();
            setGlobalState({
                isAppReady: true,
                isGuest: true,
                user: null
            });

            logoutNavigation.reset({
                index: 0,
                routes: [
                    {
                        name: "Auth"
                    }
                ]
            });
        };

        RESTService.action("Logout", null)
            .then(() => {
                handleLocalLogout();
            })
            .catch(() => {
                handleLocalLogout();
            });
    };

    const renderUserInitalInfo = () => {
        return <TouchableOpacity
            style={[
                stylesheet.userInitialInfoContainer,
                {
                    backgroundColor: colors.content.container.mid,
                    borderColor: colors.content.border.subtle,
                    paddingVertical: spaces.spacingMd,
                    paddingHorizontal: spaces.spacingMd,
                    marginBottom: spaces.spacingMd,
                    borderWidth: borders.line,
                    borderRadius: radiuses.xl
                }
            ]}
            onPress={() => {
                setShowDetail(!showDetail);
            }}
        >
            <View
                style={stylesheet.userInitialInfoAvatar}
            >
                <Image
                    source={{
                        uri: data?.profilePhotoURL
                    }}
                    resizeMode="cover"
                    style={[
                        stylesheet.profileAvatar,
                        {
                            borderRadius: radiuses.full,
                            marginRight: spaces.spacingMd
                        }
                    ]}
                />
                <View>
                    <Text
                        variant="titleLargeSize"
                    >
                        {data?.fullName}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                    >
                        {data?.mail}
                    </Text>
                </View>
            </View>
            <View
                style={[
                    stylesheet.settingButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
            >
                {
                    showDetail ? <ChevronDown
                        size={28}
                    /> : <ChevronRight
                        size={28}
                    />
                }
            </View>
        </TouchableOpacity>;
    };

    const renderPaymentStatus = () => {
        return null;
        /* return <View
            style={[
                stylesheet.paymentStatusContainer,
                {
                    paddingHorizontal: spaces.spacingMd,
                    padding: spaces.spacingMd,
                    borderWidth: borders.line,
                    borderRadius: radiuses.md,
                    borderColor: colors.content.border.subtle,
                    backgroundColor: colors.content.container.mid,
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.paymanetStatusTitle,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("duesPaymentStatus")}
                </Text>
                <WalletCards/>
            </View>
            <View
                style={[
                    stylesheet.paymentStatusContentContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.paymenetStatusStatusCard,
                        {
                            marginRight: spaces.spacingMd,
                            backgroundColor: colors.content.container.default,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.md
                        }
                    ]}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                        style={{
                            marginBottom: spaces.spacingXs
                        }}
                    >
                        {localize("paid")}
                    </Text>
                    <Text
                        variant="titleLargeSize"
                        color="success"
                    >
                        3.750 TL
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.paymenetStatusStatusCard,
                        {
                            backgroundColor: colors.content.container.default,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.md
                        }
                    ]}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                        style={{
                            marginBottom: spaces.spacingXs
                        }}
                    >
                        {localize("remainingDebt")}
                    </Text>
                    <Text
                        variant="titleLargeSize"
                        color="danger"
                    >
                        1.250 TL
                    </Text>
                </View>
            </View>
            <View
                style={[
                    stylesheet.paymentStatusStatusBarContainer,
                    {
                        borderRadius:radiuses.lg,
                        backgroundColor: colors.system.outline,
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.paymentStatusStatusBarContent,
                        {
                            borderRadius:radiuses.lg,
                            backgroundColor: colors.content.border.warning
                        }
                    ]}
                />
            </View>
            <Text
                variant="bodyMediumSize"
                color="high"
                style={[
                    stylesheet.paymentStatusStatusText,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                %75 Tamamlandı
            </Text>
        </View>; */
    };

    const renderGeneralInfo = () => {
        if(showDetail) {
            return <View
                style={[
                    stylesheet.generalInfoContainer,
                    {
                        padding: spaces.spacingMd,
                        marginBottom: spaces.spacingLg,
                        borderWidth: borders.line,
                        borderRadius: radiuses.md,
                        borderColor: colors.content.border.subtle,
                        backgroundColor: colors.content.container.mid
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("generalInformation")}
                </Text>
                <View>
                    <RowCard
                        titleVariant="titleMediumSize"
                        title={localize("editProfile")}
                        subTitle={localize("editProfileDescription")}
                        isTransparentBackground={true}
                        onPress={() => {
                            if(data) {
                                navigation.navigate("EditProfile", {
                                    user: data
                                });
                            }
                        }}
                        icon={() => <View
                            style={{
                                padding: spaces.spacingMd,
                                backgroundColor: colors.content.container.default,
                                borderRadius: radiuses.md
                            }}
                        >
                            <UserPenIcon
                                color={colors.content.icon.warning}
                            />
                        </View>}
                        iconStyle={{
                            marginRight: spaces.spacingLg
                        }}
                        rightIcon={() => <ChevronRight
                            color={colors.content.icon.high}
                        />}
                        style={{
                            marginBottom: spaces.spacingSm
                        }}
                    />
                    <RowCard
                        titleVariant="titleMediumSize"
                        title={localize("changePassword")}
                        subTitle={localize("changePasswordDescription")}
                        onPress={() => {
                            changePasswordRef.current?.open();
                        }}
                        isTransparentBackground={true}
                        icon={() => <View
                            style={{
                                padding: spaces.spacingMd,
                                backgroundColor: colors.content.container.default,
                                borderRadius: radiuses.md
                            }}
                        >
                            <Lock
                                color={colors.content.icon.warning}
                            />
                        </View>}
                        iconStyle={{
                            marginRight: spaces.spacingLg
                        }}
                        rightIcon={() => <ChevronRight
                            color={colors.content.icon.high}
                        />}
                        style={{
                            marginBottom: spaces.spacingSm
                        }}
                    />
                    <RowCard
                        titleVariant="titleMediumSize"
                        title={localize("jobOpportunityManagement")}
                        subTitle={localize("jobOpportunityManagementDescription")}
                        isTransparentBackground={true}
                        onPress={() => {
                            navigation.navigate("CampaignManagement");
                        }}
                        icon={() => <View
                            style={{
                                padding: spaces.spacingMd,
                                backgroundColor: colors.content.container.default,
                                borderRadius: radiuses.md
                            }}
                        >
                            <CircleCheckBig
                                color={colors.content.icon.warning}
                            />
                        </View>}
                        iconStyle={{
                            marginRight: spaces.spacingLg
                        }}
                        rightIcon={() => <ChevronRight
                            color={colors.content.icon.high}
                        />}
                        style={{
                            marginBottom: spaces.spacingSm
                        }}
                    />
                    <RowCard
                        titleVariant="titleMediumSize"
                        title={localize("logOut")}
                        subTitle={localize("logOutDescription")}
                        isTransparentBackground={true}
                        titleColor="danger"
                        onPress={() => {
                            setShowLogout(true);
                        }}
                        icon={() => <View
                            style={[
                                stylesheet.listIconDanger,
                                {
                                    padding: spaces.spacingMd,
                                    backgroundColor: colors.content.container.danger,
                                    borderRadius: radiuses.md
                                }
                            ]}
                        >
                            <LogOut
                                color={colors.content.icon.danger}
                            />
                        </View>}
                        iconStyle={{
                            marginRight: spaces.spacingLg
                        }}
                        rightIcon={() => <ChevronRight
                            color={colors.content.icon.high}
                        />}
                    />
                </View>
            </View>;
        } else {
            return null;
        }
    };

    const renderCommercialOpportunities = () => {
        if(data && data.myCommercialOpportunities) {
            return <View
                style={{
                    marginBottom: spaces.spacingLg
                }}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("jobOpportunity")}
                </Text>
                <TouchableOpacity
                    style={[
                        stylesheet.campaignImageWrapper,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => {
                        navigation.navigate("CampaingDetail", {
                            data: data?.myCommercialOpportunities[0] as never
                        });
                    }}
                >
                    <Image
                        source={{
                            uri:  data?.myCommercialOpportunities[0]?.coverPhotoURL
                        }}
                        resizeMode="cover"
                        style={[
                            stylesheet.campaignImageRadius,
                            {
                                borderRadius: radiuses.md
                            }
                        ]}
                    />
                    <View
                        style={[
                            stylesheet.campaignTag,
                            {
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical: spaces.spacingSm,
                                backgroundColor:colors.content.container.default,
                                borderRadius: radiuses.lg,
                                left: spaces.spacingMd,
                                top: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="high"
                            variant="titleMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.companyTitle}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.campaignBottom,
                            {
                                left: spaces.spacingMd,
                                bottom: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.title}
                        </Text>
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data?.myCommercialOpportunities[0]?.description}
                        </Text>
                    </View>
                </TouchableOpacity>
            </View>;
        } else {
            return null;
        }
    };

    const renderAboutUser = () => {
        return <View
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            <Text
                variant="titleLargeSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("about")}
            </Text>
            <Text>
                {data?.aboutMyTrade}
            </Text>
        </View>;
    };

    const renderUserCompanies = () => {
        return <View
            style={{
                marginBottom: spaces.spacingMd
            }}
        >
            <View
                style={stylesheet.userCompaniesTitleContainer}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("affiliatedCompanies")}
                </Text>
                <View
                    style={stylesheet.userCompaniesActionButtons}
                >
                    <TouchableOpacity
                        style={{
                            padding: spaces.spacingSm,
                            marginRight: spaces.spacingSm
                        }}
                        onPress={() => {
                            navigation.navigate("CreateCompany");
                        }}
                    >
                        <Plus
                            size={25}
                        />
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={{
                            padding: spaces.spacingSm,
                            marginRight: spaces.spacingSm
                        }}
                        onPress={() => {
                            navigation.navigate("MyCompanies",
                                {
                                    companies: data?.companies ? data.companies: []
                                }
                            );
                        }}
                    >
                        <Pen
                            size={20}
                        />
                    </TouchableOpacity>
                </View>
            </View>
            {
                data && data.companies && data.companies.length ?
                    data.companies.map((e) => {
                        return <RowCard
                            key={e.companyID}
                            titleVariant="titleMediumSize"
                            isTransparentBackground={true}
                            subTitle={e.companyTitle}
                            title={e.position}
                            titleColor="high"
                            icon={() => <View>
                                <Image
                                    source={e.companyLogo ? {
                                        uri: e.companyLogo
                                    } : logo}
                                    resizeMode="contain"
                                    style={[
                                        stylesheet.companyLogo,
                                        {
                                            borderRadius: radiuses.xs
                                        }
                                    ]}
                                />
                            </View>}
                            onPress={() => {
                                navigation.navigate("CompanyDetail", {
                                    companyID: e.companyID
                                });
                            }}
                            rightIcon={() => {
                                return <ChevronRight
                                    color={colors.content.icon.high}
                                />;
                            }}
                        />;
                    })
                    : null
            }
        </View>;
    };

    const renderLogoutDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeader}
            >
                <View
                    style={[
                        stylesheet.dialogIconContainer,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <LogIn
                        size={40}
                        color={colors.content.icon.danger}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.textCenter}
                >
                    {localize("confirmLogout")}
                </Text>
            </View>}
            content={localize("logOutDescription")}
            isVisible={showLogout}
            onOverlayPress={() => {
                setShowLogout(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    logout();
                },
                type:"danger",
                title: localize("logOut")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setShowLogout(false);
                }
            }}
            variant="yes-no"
        />;
    };

    const changePasswordSheet = () => {
        return <BottomSheet
            ref={changePasswordRef}
            isAutoHeight={true}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            scrollViewProps={{
                contentContainerStyle: {
                    flex: 1
                }
            }}
            renderHeader={() => <View
                style={[
                    stylesheet.bottomSheetHeader,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("changePassword")}
                </Text>
            </View>}
            renderBottom={() => <View>
                <Button
                    onPress={() => {
                        changePassword();
                    }}
                    isDisabled={passwordValidation(oldPassword) && passwordValidation(newPassword) && passwordValidation(newPasswordRepeat) && newPassword === newPasswordRepeat ? false: true}
                    customTextColor={colors.project.brandWhite}
                    title={localize("verify")}
                    customBorderColor="black"
                    spreadBehaviour="stretch"
                    customColor="black"
                    size="large"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <Button
                    onPress={() => {
                        changePasswordRef.current?.close();
                    }}
                    customBorderColor={colors.project.black}
                    title={localize("abandon")}
                    spreadBehaviour="stretch"
                    variant="outline"
                    size="large"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
            </View>}
        >
            <View
                style={stylesheet.centerContent}
            >
                <TextInput
                    title={localize("enterOldPassword")}
                    secureTextEntry={true}
                    variant="hidden"
                    icon={() => <LockKeyhole
                        size={16}
                        color={colors.content.icon.low}
                    />}
                    spreadBehaviour="stretch"
                    placeholder={localize("enterOldPasswordPlaceholder")}
                    onChangeText={(e) => {
                        setOldPassword(e);
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("enterNewPassword")}
                    secureTextEntry={true}
                    variant="hidden"
                    icon={() => <LockKeyhole
                        size={16}
                        color={colors.content.icon.low}
                    />}
                    spreadBehaviour="stretch"
                    placeholder={localize("enterNewPasswordPlaceholder")}
                    onChangeText={(e) => {
                        setNewPassword(e);
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("repeatNewPassword")}
                    secureTextEntry={true}
                    variant="hidden"
                    icon={() => <LockKeyhole
                        size={16}
                        color={colors.content.icon.low}
                    />}
                    spreadBehaviour="stretch"
                    placeholder={localize("enterNewPasswordPlaceholder")}
                    onChangeText={(e) => {
                        setNewPasswordRepeat(e);
                    }}
                    style={{
                        marginBottom: spaces.spacingXl
                    }}
                />
            </View>
        </BottomSheet>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeader}
            >
                <View
                    style={[
                        stylesheet.successIconContainer,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <LockKeyhole
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("passwordChangedSuccessfully")}
                </Text>
            </View>}
            isVisible={showSuccess}
            onOverlayPress={() => {
                setShowSuccess(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setShowSuccess(false);
                },
                title: localize("ok"),
                type: "success"
            }}
            variant="ok"
        />;
    };

    if (loading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size="large"
                color={colors.content.container.primary}
            />
        </View>;
    }

    return <PageContainer
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderUserInitalInfo()}
        {renderGeneralInfo()}
        {renderPaymentStatus()}
        {renderCommercialOpportunities()}
        {renderAboutUser()}
        {renderUserCompanies()}
        {renderLogoutDialog()}
        {changePasswordSheet()}
        {renderSuccessDialog()}
        <FooterVersionInfo/>
    </PageContainer>;
};
export default Profile;
